<template>
  <div class="post-page" :class="{ 'post-page--has-cta': ctaHref }">
    <div class="container post-page__container">
      <HcBreadcrumb>
        <li class="breadcrumb-item"><a href="/">Home</a></li>
        <li class="breadcrumb-item"><a href="/posts">Featured posts</a></li>
        <li class="breadcrumb-item active" aria-current="page" v-if="post && !loading">
          {{ post.title }}
        </li>
        <li class="breadcrumb-item active" aria-current="page" v-else-if="loading">
          Loading…
        </li>
      </HcBreadcrumb>

      <div v-if="loading" class="post-skeleton" aria-busy="true">
        <content-loader viewBox="0 0 700 200" primaryColor="#f1f5f9" secondaryColor="#e2e8f0">
          <rect x="0" y="0" rx="6" ry="6" width="100" height="20" />
          <rect x="0" y="36" rx="6" ry="6" width="520" height="28" />
          <rect x="0" y="80" rx="6" ry="6" width="160" height="40" />
          <rect x="0" y="140" rx="4" ry="4" width="640" height="12" />
          <rect x="0" y="162" rx="4" ry="4" width="600" height="12" />
          <rect x="0" y="184" rx="4" ry="4" width="560" height="12" />
        </content-loader>
      </div>

      <article v-else-if="post" class="post-article hp-animate">
        <header
          class="post-hero"
          :class="[
            'post-hero--' + category.slug,
            { 'post-hero--with-image': postImage },
          ]"
        >
          <div class="post-hero__content">
            <div class="post-hero__meta">
              <span class="post-hero__tag">{{ category.label }}</span>
              <span v-if="duration" class="post-hero__duration">{{ duration }}</span>
              <span v-if="displayDate" class="post-hero__date">{{ displayDate }}</span>
            </div>
            <h1 class="post-hero__title">{{ post.title }}</h1>
            <a
              v-if="ctaHref"
              :href="ctaHref"
              class="hp-btn hp-btn--primary post-hero__cta hp-hide-mobile"
            >{{ ctaLabel }}</a>
          </div>
          <div v-if="postImage" class="post-hero__image">
            <img :src="postImage" :alt="post.title" loading="lazy" />
          </div>
        </header>

        <div class="post-body" v-if="post.description">
          <div class="post-prose" v-html="post.description"></div>
        </div>

        <footer class="post-footer">
          <a href="/posts" class="hp-btn hp-btn--outline">← All featured posts</a>
          <a
            v-if="ctaHref"
            :href="ctaHref"
            class="hp-btn hp-btn--primary hp-hide-mobile"
          >{{ ctaLabel }}</a>
        </footer>
      </article>

      <div
        v-if="post && ctaHref"
        class="post-mobile-bar hp-show-mobile"
      >
        <a :href="ctaHref" class="hp-btn hp-btn--primary post-mobile-bar__btn">{{ ctaLabel }}</a>
      </div>
    </div>
  </div>
</template>

<script>
import Actions from '../libraries/apiActions.js';
import { ContentLoader } from 'vue-content-loader';
import HcBreadcrumb from '@/components/chrome/HcBreadcrumb.vue';
import {
  getPostCategory,
  getPostCtaLabel,
  getPostCtaHref,
  getPostDuration,
  getPostImage,
  formatPostDate,
  normalizePostLink,
} from '@/utils/postHelpers';

import '@/assets/home-page.css';
import '@/assets/post-page.css';

export default {
  name: 'Post',
  components: {
    ContentLoader,
    HcBreadcrumb,
  },
  metaInfo() {
    return {
      title: this.seo_title ? `${this.seo_title} | Hireclap` : 'Featured post | Hireclap',
      meta: [
        { name: 'description', content: this.meta_desc || '' },
        { name: 'keywords', content: this.meta_keyword || '' },
      ],
    };
  },
  data() {
    return {
      action: new Actions(),
      post: null,
      loading: true,
      isLogin: false,
      seo_title: '',
      meta_desc: '',
      meta_keyword: '',
    };
  },
  computed: {
    category() {
      return this.post ? getPostCategory(this.post) : { label: 'Post', slug: 'challenge' };
    },
    duration() {
      return this.post ? getPostDuration(this.post) : '';
    },
    displayDate() {
      if (!this.post) return '';
      const formatted = formatPostDate(this.post);
      return formatted ? `Updated ${formatted}` : '';
    },
    postImage() {
      return this.post ? getPostImage(this.post, this.$S3_url) : '';
    },
    ctaHref() {
      return this.post ? getPostCtaHref(this.post, this.isLogin) : '';
    },
    ctaLabel() {
      return this.post ? getPostCtaLabel(this.post) : 'Open';
    },
  },
  beforeMount() {
    const url = this.$route.path;
    const urlSplit = url.split('-');
    const id = urlSplit[urlSplit.length - 1];
    this.getPostDetails(id);
    this.isLogin = !!this.$cookies.get('hire-seeker');
  },
  methods: {
    getPostDetails(id) {
      this.action.getPost(id).then((res) => {
        this.post = res.post;
        this.loading = false;
        this.seo_title = this.post.seo_title || this.post.title;
        this.meta_desc = this.post.seo_description || '';
        this.meta_keyword = this.post.seo_keywords || '';
        if (this.post.link) {
          this.post.link = normalizePostLink(this.post.link);
        }
      });
    },
  },
};
</script>
